import { useMemo } from 'react'
import { collection, orderBy, query } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { useFirestoreCollection } from './useFirestore'
import { useAuth } from './useAuth'
import { useAdminPendingDocs } from './useAdminPendingDocs'
import type { Feedback } from '../types'

const EMPTY: Feedback[] = []

// Every feedback submission, newest first. Rules make the collection
// admin-read-only, so non-admins run no query at all rather than hitting a
// permission error from the nav chrome.
export function useAdminFeedback() {
  const { isAdmin } = useAuth()

  const feedbackQuery = useMemo(
    () =>
      isAdmin
        ? (query(collection(db, 'feedback'), orderBy('createdAt', 'desc')) as Query<Feedback>)
        : null,
    [isAdmin],
  )

  const { data, loading, error } = useFirestoreCollection<Feedback>(feedbackQuery)

  return { feedback: isAdmin ? data : EMPTY, loading, error }
}

// Submissions the /process-feedback pipeline hasn't triaged yet. Clients create
// feedback without a status; onFeedbackCreated stamps `pending` server-side, so
// the badge can listen to the pending slice instead of every feedback doc and
// its logs.
export function useAdminPendingFeedbackCount(): number {
  return useAdminPendingDocs<Feedback>('feedback').length
}
